import React, { createContext, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

export const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: 'light' });

const buildTheme = (mode) => createTheme({
    palette: {
        mode,
        primary: { main: '#6753E7', dark: '#4F3CC5', light: '#A99DF7', contrastText: '#FFFFFF' },
        secondary: { main: '#00A99D', dark: '#007F77', light: '#61D5CC', contrastText: '#FFFFFF' },
        success: { main: '#1DBEA8', dark: '#128A7A', light: '#79E7DD' },
        warning: { main: '#F5A623', dark: '#B66D00', light: '#FFCD6A' },
        error: { main: '#F06879', dark: '#B73548', light: '#FFB0BA' },
        info: { main: '#55A9EC', dark: '#276FB0', light: '#88CAFF' },
        background: mode === 'light'
            ? { default: '#F5F7FB', paper: '#FFFFFF' }
            : { default: '#0B1828', paper: '#13283D' },
        text: mode === 'light'
            ? { primary: '#102D47', secondary: '#6F8190' }
            : { primary: '#F5FAFD', secondary: '#91A6B8' },
        divider: mode === 'light' ? 'rgba(16,45,71,.10)' : 'rgba(255,255,255,.10)',
        action: mode === 'light'
            ? { hover: 'rgba(103,83,231,.055)', selected: 'rgba(103,83,231,.10)' }
            : { hover: 'rgba(169,157,247,.08)', selected: 'rgba(169,157,247,.14)' },
    },
    shape: { borderRadius: 14 },
    spacing: 8,
    typography: {
        fontFamily: 'Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        h1: { fontWeight: 760, letterSpacing: '-0.035em' },
        h2: { fontWeight: 750, letterSpacing: '-0.03em' },
        h3: { fontWeight: 730, letterSpacing: '-0.025em' },
        h4: { fontWeight: 720, letterSpacing: '-0.02em' },
        h5: { fontWeight: 700, letterSpacing: '-0.015em' },
        h6: { fontWeight: 680 },
        button: { fontWeight: 650, textTransform: 'none', letterSpacing: 0 },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: { minWidth: 320 },
                '*': { boxSizing: 'border-box' },
                '::selection': { backgroundColor: 'rgba(103,83,231,.24)' },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: 'none' },
                outlined: { borderColor: mode === 'light' ? 'rgba(16,45,71,.10)' : 'rgba(255,255,255,.10)' },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    border: `1px solid ${mode === 'light' ? 'rgba(16,45,71,.08)' : 'rgba(255,255,255,.09)'}`,
                    boxShadow: mode === 'light' ? '0 14px 40px rgba(16,45,71,.065)' : '0 18px 44px rgba(0,0,0,.24)',
                },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: { borderRadius: 14, minHeight: 46, paddingInline: 22, fontSize: 15 },
                containedPrimary: { boxShadow: '0 9px 22px rgba(103,83,231,.24)' },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: { width: 44, height: 44, borderRadius: 14 },
            },
        },
        MuiTextField: { defaultProps: { variant: 'outlined' } },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 11,
                    backgroundColor: mode === 'light' ? '#FFFFFF' : 'rgba(255,255,255,.025)',
                },
            },
        },
        MuiChip: { styleOverrides: { root: { borderRadius: 8, fontWeight: 620 } } },
        MuiTableCell: {
            styleOverrides: {
                head: { fontWeight: 700, color: mode === 'light' ? '#506779' : '#C3D0DA', backgroundColor: mode === 'light' ? '#F0F2FA' : '#172D43' },
                root: { borderColor: mode === 'light' ? 'rgba(16,45,71,.08)' : 'rgba(255,255,255,.08)' },
            },
        },
        MuiDialog: { styleOverrides: { paper: { borderRadius: 20 } } },
        MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
        MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, fontSize: 12 } } },
    },
});

const SmartPayThemeProvider = ({ children }) => {
    const [mode, setMode] = useState(() => localStorage.getItem('smartpay-color-mode') || 'light');
    const contextValue = useMemo(() => ({
        mode,
        toggleColorMode: () => setMode((current) => {
            const next = current === 'light' ? 'dark' : 'light';
            localStorage.setItem('smartpay-color-mode', next);
            return next;
        }),
    }), [mode]);
    const theme = useMemo(() => buildTheme(mode), [mode]);

    return (
        <ColorModeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default SmartPayThemeProvider;
